type FeatureFlagProvider = 'devcycle' | 'posthog';

type FeatureFlagBase = {
  key: string;
  friendlyName: string;
  provider: FeatureFlagProvider;
  providerDashboardUrl: string;
};

type BooleanFeatureFlagConfig = FeatureFlagBase & {
  type: 'boolean';
  defaultValue: boolean;
};

type MultivariateFeatureFlagConfig<V extends string = string> = FeatureFlagBase & {
  type: 'multivariate';
  variants: readonly [V, ...V[]];
  defaultValue: V;
};

export type FeatureFlagConfig = BooleanFeatureFlagConfig | MultivariateFeatureFlagConfig;

export const featureFlags = {
  emailVerificationNeededForWithdraw: {
    key: 'email-verification-needed-for-withdraw',
    friendlyName: 'Email verification needed for withdraw',
    provider: 'devcycle',
    type: 'boolean',
    defaultValue: false,
    providerDashboardUrl:
      'https://app.devcycle.com/o/org_6iwD05e0WavDcZzd/p/credit/features/6a38ee2f66ce007fe9ef6009/manage-feature',
  },
  newProductHomepage: {
    key: 'new-product-homepage',
    friendlyName: 'New product homepage',
    provider: 'posthog',
    type: 'boolean',
    defaultValue: false,
    providerDashboardUrl: 'https://eu.posthog.com/project/222704/feature_flags/225219',
  },
  // TODO: Delete this flag in AC-1147.
  seApplicationLoanPurposeStep: {
    key: 'se-application-loan-purpose-step',
    friendlyName: 'SE application loan purpose step',
    provider: 'posthog',
    type: 'multivariate',
    variants: ['control', 'test'],
    defaultValue: 'control',
    providerDashboardUrl: 'https://eu.posthog.com/project/222704/feature_flags/280062',
  },
  maintenanceInProgress: {
    key: 'maintenance-in-progress',
    friendlyName: 'Maintenance in progress',
    provider: 'devcycle',
    type: 'boolean',
    defaultValue: false,
    providerDashboardUrl:
      'https://app.devcycle.com/o/org_6iwD05e0WavDcZzd/p/credit/features/669fa3bc6563d967c9115b33/manage-feature',
  },
  bankIdV6Sandbox: {
    key: 'bank-id-v6-sandbox',
    friendlyName: 'BankID v6 sandbox',
    provider: 'devcycle',
    type: 'boolean',
    defaultValue: false,
    providerDashboardUrl:
      'https://app.devcycle.com/o/org_6iwD05e0WavDcZzd/p/credit/features/6606c3f70b7f52aff7d5639e/manage-feature',
  },
} as const satisfies Record<string, FeatureFlagConfig>;

export type FeatureFlagId = keyof typeof featureFlags;

export const getFeatureFlagConfig = (flagId: FeatureFlagId): FeatureFlagConfig => featureFlags[flagId];

export type FeatureFlagValueForConfig<C extends FeatureFlagConfig> = C extends BooleanFeatureFlagConfig
  ? boolean
  : C extends MultivariateFeatureFlagConfig
    ? C['variants'][number]
    : never;

export type FeatureFlagValue<T extends FeatureFlagId> = FeatureFlagValueForConfig<(typeof featureFlags)[T]>;

export const featureFlagList = Object.values(featureFlags);
